/**
 * useBoltData Hook
 *
 * Custom hook for fetching Bolt AI data from production APIs.
 * Provides loading states and error handling without mock fallbacks.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import boltAPI from '../services/boltApi';
import { logger } from '../lib/logger';

const EMPTY_FILTERS = {};

// Configuration
const CONFIG = {
  // Refetch interval in milliseconds (5 minutes)
  refetchInterval: 5 * 60 * 1000,
};

/**
 * Hook for fetching Bolt status
 */
export function useBoltStatus(jobsiteId = null) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const data = await boltAPI.getStatus(jobsiteId);
      setStatus(data);
      setError(null);
    } catch (err) {
      logger.error('[useBoltStatus] API error', { error: err?.message });
      setStatus(null);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [jobsiteId]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, CONFIG.refetchInterval);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  return { status, loading, error, refetch: fetchStatus };
}

/**
 * Hook for fetching Bolt documents
 */
export function useBoltDocuments(filters = EMPTY_FILTERS) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const filtersRef = useRef(filters);

  // Update ref when filters change
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await boltAPI.getDocuments(filtersRef.current);
      setDocuments(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      logger.error('[useBoltDocuments] API error', { error: err?.message });
      setDocuments([]);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments, filters]);

  // Auto-refetch when documents are in processing states
  useEffect(() => {
    const NON_TERMINAL = ['pending', 'queued', 'indexing'];
    const hasProcessing = documents.some((d) => NON_TERMINAL.includes(d.status));
    if (!hasProcessing) return;
    const interval = setInterval(fetchDocuments, 10000);
    return () => clearInterval(interval);
  }, [documents, fetchDocuments]);

  // Document operations
  const uploadDocument = useCallback(async (file, metadata) => {
    const result = await boltAPI.uploadDocument(file, metadata);
    await fetchDocuments(); // Refresh list
    return result;
  }, [fetchDocuments]);

  const deleteDocuments = useCallback(async (documentIds) => {
    for (const id of documentIds) {
      await boltAPI.deleteDocument(id);
    }
    await fetchDocuments();
    return { success: true };
  }, [fetchDocuments]);

  const reindexDocuments = useCallback(async (documentIds) => {
    for (const id of documentIds) {
      await boltAPI.reindexDocument(id);
    }
    await fetchDocuments();
    return { success: true };
  }, [fetchDocuments]);

  return {
    documents,
    loading,
    error,
    refetch: fetchDocuments,
    uploadDocument,
    deleteDocuments,
    reindexDocuments,
  };
}

/**
 * Hook for fetching Bolt analytics
 */
export function useBoltAnalytics(filters = EMPTY_FILTERS) {
  const [analytics, setAnalytics] = useState(null);
  const [heroMetrics, setHeroMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const data = await boltAPI.getAnalytics(filters);

      // Transform API response to expected format if needed
      setAnalytics(data || null);

      // Generate hero metrics from analytics
      if (data) {
        setHeroMetrics({
          totalQueries: {
            value: data.queryVolume?.thisWeek || 0,
            trend: '+0%',
            direction: 'up',
            label: 'Queries This Week',
          },
          activeUsers: {
            value: data.workerAdoption?.activeUsers || 0,
            trend: '+0',
            direction: 'up',
            label: 'Active Users',
          },
          queriesPerUser: {
            value: data.workerAdoption?.activeUsers > 0
              ? (data.queryVolume?.thisWeek / data.workerAdoption?.activeUsers || 0).toFixed(1)
              : 0,
            trend: '+0',
            direction: 'up',
            label: 'Queries/User/Day',
          },
          answerSuccess: {
            value: data.feedback?.positiveRate || 0,
            trend: '+0%',
            direction: 'up',
            label: 'Answer Success',
          },
        });
      } else {
        setHeroMetrics(null);
      }

      setError(null);
    } catch (err) {
      logger.error('[useBoltAnalytics] API error', { error: err?.message });
      setAnalytics(null);
      setHeroMetrics(null);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, CONFIG.refetchInterval);
    return () => clearInterval(interval);
  }, [fetchAnalytics]);

  return { analytics, heroMetrics, loading, error, refetch: fetchAnalytics };
}

/**
 * Hook for fetching recent queries
 * Note: This would need a dedicated endpoint in vh-api
 */
export function useBoltRecentQueries() {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQueries = async () => {
      try {
        setLoading(true);
        if (typeof boltAPI.getRecentQueries !== 'function') {
          logger.warn('[useBoltRecentQueries] Recent queries endpoint not wired');
          setQueries([]);
          setError(new Error('Recent queries endpoint not wired'));
          return;
        }
        const data = await boltAPI.getRecentQueries();
        setQueries(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err) {
        logger.error('[useBoltRecentQueries] API error', { error: err?.message });
        setQueries([]);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchQueries();
  }, []);

  return { queries, loading, error };
}

/**
 * Hook for fetching AI suggestions
 * Note: This would need a dedicated endpoint in vh-api
 */
export function useBoltSuggestions() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const data = await boltAPI.getAISuggestions();
        setSuggestions(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err) {
        logger.error('[useBoltSuggestions] API error', { error: err?.message });
        setSuggestions([]);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, []);

  const dismissSuggestion = useCallback((suggestionId) => {
    setSuggestions((prev) => prev.filter((s) => s.id !== suggestionId));
  }, []);

  return { suggestions, loading, error, dismissSuggestion };
}

/**
 * BOLT persisted conversations for a jobsite (or org-wide when jobsiteId is null)
 */
export function useBoltConversations(jobsiteId = null) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const jobsiteRef = useRef(jobsiteId);

  useEffect(() => {
    jobsiteRef.current = jobsiteId;
  }, [jobsiteId]);

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await boltAPI.listConversations(jobsiteRef.current, 1, 20);
      setConversations(Array.isArray(data?.conversations) ? data.conversations : []);
      setError(null);
    } catch (err) {
      logger.error('[useBoltConversations] API error', { error: err?.message });
      setConversations([]);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations, jobsiteId]);

  const deleteConversation = useCallback(
    async (conversationId) => {
      await boltAPI.deleteConversation(conversationId);
      await fetchConversations();
    },
    [fetchConversations]
  );

  const renameConversation = useCallback(
    async (conversationId, title) => {
      await boltAPI.renameConversation(conversationId, title);
      await fetchConversations();
    },
    [fetchConversations]
  );

  return {
    conversations,
    loading,
    error,
    refetch: fetchConversations,
    deleteConversation,
    renameConversation,
  };
}

// Export all hooks
export default {
  useBoltStatus,
  useBoltDocuments,
  useBoltAnalytics,
  useBoltRecentQueries,
  useBoltSuggestions,
  useBoltConversations,
};
